import { Alert, Button, Checkbox, Dialog, FormControlLabel, Typography } from '@mui/material';
import { Box, Stack, SystemStyleObject } from '@mui/system';
import React from 'react';

import {
  useCreateBeneficiaryVirtualAccountMutation,
  useGetBeneficiaryVirtualAccountsQuery,
} from '@/api/v1';
import DepositIcon from '@/assets/icons/deposit-icon.svg?react';
import SendIcon from '@/assets/icons/send-icon.svg?react';
import WalletIcon from '@/assets/icons/wallet.svg?react';
import { LoadingButton } from '@/components/molecules/LoadingButton';
import { ApplicationModal } from '@/components/organisms/ApplicationModal';
import { AML_POLICY_URL, PRIVACY_POLICY_URL, TERMS_AND_CONDITIONS_URL } from '@/constants';
import { useCustomer } from '@/hooks/useCustomer';
import { useToast } from '@/hooks/useToast';
import { useTranslator } from '@/hooks/useTranslator';

interface Props {
  readonly open: boolean;
  readonly beneficiaryId: string;
  onClose(): void;
  onSuccess(): void;
}

interface Benefit {
  readonly key: string;
  readonly Icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
}

const BENEFITS: readonly Benefit[] = [
  { key: 'USD_ACCOUNT_BENEFIT_1', Icon: DepositIcon },
  { key: 'USD_ACCOUNT_BENEFIT_2', Icon: SendIcon },
  { key: 'USD_ACCOUNT_BENEFIT_3', Icon: WalletIcon },
];

const POLLING_TIMEOUT_MS = 30000;
const POLLING_INTERVAL_MS = 2500;

interface LinkConfig {
  readonly text: string;
  readonly href: string;
}

const interpolateWithLinks = (
  template: string,
  links: readonly LinkConfig[],
): React.ReactNode[] => {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;

  links.forEach((link: LinkConfig, index: number): void => {
    const placeholder = `{${index}}`;
    const placeholderIndex = template.indexOf(placeholder, lastIndex);

    if (placeholderIndex !== -1) {
      if (placeholderIndex > lastIndex) {
        parts.push(template.slice(lastIndex, placeholderIndex));
      }

      parts.push(
        <Typography
          component="a"
          variant="body2"
          key={index}
          href={link.href}
          target="_blank"
          rel="noopener noreferrer">
          {link.text}
        </Typography>,
      );

      lastIndex = placeholderIndex + placeholder.length;
    }
  });

  if (lastIndex < template.length) {
    parts.push(template.slice(lastIndex));
  }

  return parts;
};

type ModalStep = 'benefits' | 'terms';

interface CertificationsState {
  termsAndConditions: boolean;
  authority: boolean;
  customerOnlyUse: boolean;
  authorizedLawfulUse: boolean;
}

export const CreateUsdAccountModal: React.FC<Props> = (props: Props): React.ReactElement => {
  const { open, beneficiaryId, onClose, onSuccess } = props;
  const tr = useTranslator();
  const toast = useToast();
  const customer = useCustomer();
  const integratorName = customer.business?.name || customer.businessInfo?.commercialName || '';

  const [hasTimedOut, setHasTimedOut] = React.useState(false);
  const [currentStep, setCurrentStep] = React.useState<ModalStep>('benefits');
  const [certifications, setCertifications] = React.useState<CertificationsState>({
    termsAndConditions: false,
    authority: false,
    customerOnlyUse: false,
    authorizedLawfulUse: false,
  });

  const allCertificationsAccepted = Object.values(certifications).every(Boolean);

  const [
    createBeneficiaryUSDVirtualAccount,
    { isLoading: isCreating, isSuccess: hasStartedCreation, reset: resetMutation },
  ] = useCreateBeneficiaryVirtualAccountMutation();

  const { data: virtualAccounts = [], isError: isPollingError } =
    useGetBeneficiaryVirtualAccountsQuery(beneficiaryId, {
      skip: !hasStartedCreation || hasTimedOut,
      pollingInterval: hasStartedCreation && !hasTimedOut ? POLLING_INTERVAL_MS : 0,
      refetchOnMountOrArgChange: true,
    });

  const accountCreated = virtualAccounts.length > 0;
  const isPolling = hasStartedCreation && !accountCreated && !hasTimedOut && !isPollingError;
  const isLoading = isCreating || isPolling;
  const showTimeout = hasTimedOut;
  const showError = isPollingError && !accountCreated;
  const showForm = !isLoading && !showTimeout && !showError;

  // Detect account creation success
  React.useEffect((): void => {
    if (hasStartedCreation && accountCreated) {
      onSuccess();
      onClose();
    }
  }, [hasStartedCreation, accountCreated, onSuccess, onClose]);

  // Handle timeout
  React.useEffect(() => {
    if (isPolling) {
      const timeout = setTimeout((): void => {
        setHasTimedOut(true);
      }, POLLING_TIMEOUT_MS);
      return (): void => clearTimeout(timeout);
    }
  }, [isPolling]);

  // Reset state when modal opens (only on open transition, not on every render)
  const prevOpenRef = React.useRef(false);
  React.useEffect((): void => {
    // Only reset when modal transitions from closed to open
    if (open && !prevOpenRef.current) {
      resetMutation();
      setHasTimedOut(false);
      setCurrentStep('benefits');
      setCertifications({
        termsAndConditions: false,
        authority: false,
        customerOnlyUse: false,
        authorizedLawfulUse: false,
      });
    }
    prevOpenRef.current = open;
  }, [open, resetMutation]);

  const handleContinue = React.useCallback((): void => {
    setCurrentStep('terms');
  }, []);

  const handleBack = React.useCallback((): void => {
    setCurrentStep('benefits');
  }, []);

  const handleCertificationChange = React.useCallback(
    (key: keyof CertificationsState) =>
      (event: React.ChangeEvent<HTMLInputElement>): void => {
        setCertifications(
          (prev: CertificationsState): CertificationsState => ({
            ...prev,
            [key]: event.target.checked,
          }),
        );
      },
    [],
  );

  const handleAcceptAllChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const checked = event.target.checked;
      setCertifications({
        termsAndConditions: checked,
        authority: checked,
        customerOnlyUse: checked,
        authorizedLawfulUse: checked,
      });
    },
    [],
  );

  const handleConfirm = React.useCallback(async (): Promise<void> => {
    try {
      await createBeneficiaryUSDVirtualAccount({
        beneficiaryId,
      }).unwrap();
      // isSuccess becomes true automatically, which starts polling
    } catch {
      toast.show(tr('AN_ERROR_OCCURRED'), 'error');
    }
  }, [createBeneficiaryUSDVirtualAccount, beneficiaryId, toast, tr]);

  const handleClose = React.useCallback((): void => {
    resetMutation();
    setHasTimedOut(false);
    onClose();
  }, [resetMutation, onClose]);

  const getDescription = (): string | undefined => {
    if (!showForm) {
      return undefined;
    }

    if (currentStep === 'benefits') {
      return tr('CREATE_USD_ACCOUNT_DESCRIPTION');
    }

    return tr('USD_ACCOUNT_TERMS_DESCRIPTION');
  };

  return (
    <Dialog open={open} onClose={isLoading ? undefined : handleClose}>
      <ApplicationModal
        title={tr('CREATE_USD_ACCOUNT')}
        description={getDescription()}
        onClose={isLoading ? undefined : handleClose}>
        {showTimeout ? (
          <Stack gap={3} alignItems="center">
            <Alert severity="error" sx={styles.fullWidth}>
              {tr('USD_ACCOUNT_CREATION_TIMEOUT')}
            </Alert>
            <Button color="inherit" onClick={handleClose}>
              {tr('CLOSE')}
            </Button>
          </Stack>
        ) : showError ? (
          <Stack alignItems="center" gap={3}>
            <Alert severity="error" sx={styles.fullWidth}>
              {tr('USD_ACCOUNT_CREATION_ERROR')}
            </Alert>
            <Stack alignItems="center" gap={2} sx={styles.fullWidth}>
              <LoadingButton
                variant="contained"
                loading={isLoading}
                onClick={handleConfirm}
                fullWidth>
                {isLoading ? `${tr('CREATING')}...` : tr('RETRY')}
              </LoadingButton>
              <Button color="inherit" onClick={handleClose}>
                {tr('CLOSE')}
              </Button>
            </Stack>
          </Stack>
        ) : currentStep === 'benefits' ? (
          <>
            <Stack sx={styles.benefitsList}>
              {BENEFITS.map(
                ({ key, Icon }: Benefit): React.ReactElement => (
                  <Box key={key} sx={styles.benefitItem}>
                    <Box sx={styles.iconWrapper}>
                      <Icon width={20} height={20} />
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {tr(key)}
                    </Typography>
                  </Box>
                ),
              )}
            </Stack>
            <Button variant="contained" onClick={handleContinue} fullWidth>
              {tr('CONTINUE')}
            </Button>
          </>
        ) : (
          <>
            <FormControlLabel
              sx={styles.acceptAllCheckbox}
              control={
                <Checkbox
                  checked={allCertificationsAccepted}
                  indeterminate={
                    !allCertificationsAccepted && Object.values(certifications).some(Boolean)
                  }
                  onChange={handleAcceptAllChange}
                />
              }
              label={
                <Typography variant="body2" color="text.secondary" fontWeight="bold">
                  {tr('ACCEPT_ALL')}
                </Typography>
              }
            />
            <Stack sx={styles.certificationsList}>
              <FormControlLabel
                sx={styles.certificationCheckbox}
                control={
                  <Checkbox
                    checked={certifications.termsAndConditions}
                    onChange={handleCertificationChange('termsAndConditions')}
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    <Typography variant="body2" component="span" fontWeight="bold">
                      {tr('USD_ACCOUNT_CERT_TERMS_LABEL')}
                    </Typography>
                    {' - '}
                    {interpolateWithLinks(tr('USD_ACCOUNT_CERT_TERMS'), [
                      { text: tr('USD_ACCOUNT_CERT_TERMS_LINK'), href: TERMS_AND_CONDITIONS_URL },
                      { text: tr('USD_ACCOUNT_CERT_TERMS_PRIVACY_LINK'), href: PRIVACY_POLICY_URL },
                    ])}
                  </Typography>
                }
              />
              <FormControlLabel
                sx={styles.certificationCheckbox}
                control={
                  <Checkbox
                    checked={certifications.authority}
                    onChange={handleCertificationChange('authority')}
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    <Typography variant="body2" component="span" fontWeight="bold">
                      {tr('USD_ACCOUNT_CERT_AUTHORITY_LABEL')}
                    </Typography>
                    {' - '}
                    {tr('USD_ACCOUNT_CERT_AUTHORITY', integratorName)}
                  </Typography>
                }
              />
              <FormControlLabel
                sx={styles.certificationCheckbox}
                control={
                  <Checkbox
                    checked={certifications.customerOnlyUse}
                    onChange={handleCertificationChange('customerOnlyUse')}
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    <Typography variant="body2" component="span" fontWeight="bold">
                      {tr('USD_ACCOUNT_CERT_CUSTOMER_ONLY_USE_LABEL')}
                    </Typography>
                    {' - '}
                    {tr('USD_ACCOUNT_CERT_CUSTOMER_ONLY_USE')}
                  </Typography>
                }
              />
              <FormControlLabel
                sx={styles.certificationCheckbox}
                control={
                  <Checkbox
                    checked={certifications.authorizedLawfulUse}
                    onChange={handleCertificationChange('authorizedLawfulUse')}
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    <Typography variant="body2" component="span" fontWeight="bold">
                      {tr('USD_ACCOUNT_CERT_LAWFUL_USE_LABEL')}
                    </Typography>
                    {' - '}
                    {interpolateWithLinks(tr('USD_ACCOUNT_CERT_LAWFUL_USE'), [
                      { text: tr('USD_ACCOUNT_CERT_LAWFUL_USE_LINK'), href: AML_POLICY_URL },
                    ])}
                  </Typography>
                }
              />
            </Stack>
            <Stack direction="row" gap={2} sx={styles.fullWidth}>
              <Button variant="outlined" color="inherit" onClick={handleBack} fullWidth>
                {tr('BACK')}
              </Button>
              <LoadingButton
                variant="contained"
                loading={isLoading}
                disabled={!allCertificationsAccepted}
                onClick={handleConfirm}
                fullWidth>
                {isLoading ? `${tr('CREATING')}...` : tr('CREATE')}
              </LoadingButton>
            </Stack>
          </>
        )}
      </ApplicationModal>
    </Dialog>
  );
};

const styles: Record<string, SystemStyleObject> = {
  benefitsList: {
    gap: 4,
    mb: 4,
  },
  benefitItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 2,
  },
  iconWrapper: {
    backgroundColor: 'custom.Primary1Background',
    borderRadius: '50%',
    width: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    '& svg': {
      color: 'primary.main',
    },
  },
  fullWidth: {
    width: '100%',
  },
  certificationsList: {
    gap: 4,
    mb: 4,
  },
  acceptAllCheckbox: {
    alignItems: 'center',
    gap: 1,
    mb: 2,
  },
  certificationCheckbox: {
    alignItems: 'flex-start',
    gap: 1,
  },
};
